import type { RpcOptions, UnaryCall } from '@protobuf-ts/runtime-rpc'
import type { Error as GrpcError } from '@vpn-tg/vue-client-grpc/pkg/error/error'
import { useAuthStore } from '@/modules/auth/stores/authStore'
import { checkTokenValidation } from '@/utils/checkTokenValidation'
import { EventLogger } from '@/utils/EventLogger'
import { tGlobal } from '@/plugins/i18n'

export interface ResponseWithError {
  error?: GrpcError
}

const logout = async (reason?: string): Promise<void> => {
  console.log('[GRPC] Вызван logout, причина:', reason)
  const authStore = useAuthStore()
  await authStore.logout(reason)
  console.log('[GRPC] logout завершён')
}

/** Обрыв сети / 504 / таймаут шлюза — не ответ бэка, в UI не показываем сырой Failed to fetch. */
const transportErrorPayload = (err: unknown): ResponseWithError => ({
  error: {
    message: tGlobal('errors.connectionLost'),
    systemMessage: err instanceof Error && err.stack ? err.stack : String(err),
  } as GrpcError,
})

export const grpcCall = async <RequestType extends object, ResponseType extends ResponseWithError>(
  method: (args?: RequestType, options?: RpcOptions) => UnaryCall<RequestType, ResponseType>,
  caller?: string,
): Promise<[boolean, ResponseType]> => {
  // FIX: локальная обёртка, чтобы не логировать сбои createLog обратно в бекенд (рекурсия)
  const logError = (...args: unknown[]) => {
    if (caller !== 'createLog') EventLogger.error('grpcCall', ...args)
  }

  try {
    // не валидируем токен для логирования (чтобы не попасть в рекурсию)
    if (caller && !['telegramLogin', 'telegramOIDCLogin', 'refresh', 'createLog', 'signUp', 'signIn', 'validateMagicLink', 'getSettings', 'getTariffs'].includes(caller)) {
      const accessToken = useAuthStore().accessToken
      const isValid = await checkTokenValidation(accessToken, 'grpc-call-is-valid')
      console.log('[GRPC] Результат проверки токена:', isValid)
      if (!isValid) {
        console.log('[GRPC] Токен невалиден, вызываю logout')
        await logout(`Проверка токена вернула false (метод: ${caller})`)
        return [false, {} as ResponseType]
      }
    }

    let call
    try {
      call = method()
    } catch (err) {
      console.error('[GRPC] Ошибка при вызове method()', err)
      EventLogger.error('grpcCall', ['Ошибка при вызове method()', err])
      return [false, transportErrorPayload(err) as ResponseType]
    }

    if (!call || typeof call !== 'object' || typeof call.response !== 'object' || typeof call.response.then !== 'function') {
      console.error('[GRPC] method() вернул НЕ объект UnaryCall с .response:', call)
      EventLogger.error('grpcCall', ['gRPC client method() вернул невалидный объект', call])
      return [false, transportErrorPayload(call) as ResponseType]
    }

    let result
    try {
      result = await call.response
    } catch (err) {
      console.error('[GRPC] Ошибка в call.response:', err)
      EventLogger.error('grpcCall', ['Ошибка в call.response', err])
      return [false, transportErrorPayload(err) as ResponseType]
    }

    // Группированное логирование request/response (без бинарных данных); в prod-сборке esbuild вырезает console-вызовы
    const methodName = call.method?.name || caller || 'unknown'
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = result as any
    const logResult = caller === 'getGuide' && r.guide
      ? { ...r, guide: { id: r.guide.id, name: r.guide.name, pdfSize: r.guide.pdfFile?.length } }
      : result
    console.group(methodName, new Date().toLocaleString())
    console.log('%c request: ', 'background: #222; color: #bada55', call.request)
    console.log('%c response: ', 'background: #222; color: #bada55', logResult)
    console.groupEnd()

    if (result.error) {
      logError('Результат содержит ошибку', result.error)
      return [false, result]
    }

    return [true, result]
  } catch (error: unknown) {
    console.log('%c catch error: ', 'background: #222; color: #bada55', error)
    logError('grpcCall CATCH', error)
    const msg = error instanceof Error ? error.message : String(error)

    if (msg.includes('token is invalid') || msg.includes('token is expired')) {
      await logout(`Исключение gRPC (метод ${caller || 'unknown'}): ${msg}`)
    }

    return [false, transportErrorPayload(error) as ResponseType]
  }
}
